import { sb } from '../supabase';
import { isInternalReferrer } from './internalReferrers';

// Where a visitor came from — owner decision 2026-11-01, replacing the
// tab-only rule of 2026-09-18 (which protected nothing that reads it: no code
// or report read profiles.utm_*, and 0 of 180 profiles were ever tagged).
//
//   first touch — the first visit with a KNOWN source in the last 30 days
//                 (UTMs, click ids, or an outside referrer); a direct visit
//                 is kept only until a known source arrives;
//   last touch  — the latest visit with a known source; a direct visit never
//                 overwrites it.
// Both expire 30 days after they were set. Both are saved at sign-up and on
// every store order through record_attribution() (20261101950000), so an
// order placed days after the ad click is still credited to the ad.

const KEY = 'maabar.attr.v1';
const WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
const UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const CLICK = ['gclid', 'fbclid', 'ttclid'];
// Coming back from our own flows (sign-in, OAuth, payment, bot check) is not
// a new source — one list shared with the session tracker (internalReferrers.js).

function read() {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || 'null') || {};
    const fresh = (t) => (t && Date.now() - Date.parse(t.at) < WINDOW_MS ? t : null);
    return { first: fresh(s.first), last: fresh(s.last) };
  } catch { return { first: null, last: null }; }
}

function write(s) {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* storage blocked: attribution is best effort */ }
}

// The touch this landing represents.
export function touchFrom(loc = window.location, referrer = document.referrer, now = new Date()) {
  const t = { at: now.toISOString(), landing_path: loc.pathname };
  try {
    const q = new URLSearchParams(loc.search || '');
    for (const k of [...UTM, ...CLICK]) {
      const v = (q.get(k) || '').trim();
      if (v) t[k] = v.slice(0, 120);
    }
  } catch { /* no params */ }
  try {
    const host = referrer ? new URL(referrer).hostname : '';
    if (host && !isInternalReferrer(referrer, loc.hostname)) t.referrer_domain = host;
  } catch { /* bad referrer */ }
  t.channel = UTM.some((k) => t[k]) ? 'utm' : CLICK.some((k) => t[k]) ? 'click' : t.referrer_domain ? 'referrer' : 'direct';
  return t;
}

// Call once per page load.
export function noteLanding(touch = touchFrom()) {
  const s = read();
  const known = touch.channel !== 'direct';
  if (!s.first || (s.first.channel === 'direct' && known)) s.first = touch;
  if (known) s.last = touch;
  write(s);
  return s;
}

export function currentAttribution() { return read(); }

// The first touch's UTMs, for the sign-up metadata set_profile_utm reads.
export function firstTouchUtm() {
  const f = read().first;
  const out = {};
  if (f) for (const k of ['utm_source', 'utm_medium', 'utm_campaign']) if (f[k]) out[k] = f[k];
  return out;
}

async function record(kind, subjectId, anonId) {
  const { first, last } = read();
  const { data, error } = await sb.rpc('record_attribution', {
    p_kind: kind, p_subject: subjectId, p_anon: anonId || null, p_first: first || {}, p_last: last || {},
  });
  if (error) throw error;
  return data;
}

// A new account: once per user per browser. The server refuses accounts older than 24 h.
export async function recordSignupAttribution(userId, anonId) {
  try {
    const flag = `maabar.attr.signup.${userId}`;
    try { if (localStorage.getItem(flag)) return; } catch { /* no flag storage: the server dedupes */ }
    const r = await record('signup', userId, anonId);
    try { localStorage.setItem(flag, r || '1'); } catch { /* fine */ }
  } catch (err) {
    console.warn('[attribution] signup not recorded:', err?.message || err);
  }
}

// A store order: called right after it is created, before payment.
export async function recordOrderAttribution(orderId, anonId) {
  try { await record('order', orderId, anonId); } catch (err) {
    console.warn('[attribution] order not recorded:', err?.message || err);
  }
}
